import api from "./api";

/**
 * Helper to retrieve the employee code from storage.
 * ✅ Standardized to ONE key used everywhere: "emp_code"
 */
const getStoredEmpCode = () => localStorage.getItem("emp_code");

/* ===================================================================
   ADMIN AUTH SERVICES
   =================================================================== */

/**
 * Admin/HR Login
 */
export const adminLogin = async (payload) => {
  return api.post("/auth/login", payload);
};

/* ===================================================================
   1. DASHBOARD STATISTICS (SAFE + FAULT-TOLERANT)
   =================================================================== */

/**
 * Fetches admin dashboard statistics.
 * Uses HR dashboard endpoints that require admin/hr authentication
 */
export const getAdminDashboardStats = async () => {
  try {
    console.log("🔍 Fetching admin dashboard stats...");
    console.log("Token:", localStorage.getItem("access_token"));
    console.log("Role:", localStorage.getItem("role"));
    
    const results = await Promise.allSettled([
      api.get("/dashboard/total-employees"),
      api.get("/dashboard/present-today"),
      api.get("/dashboard/on-leave")
    ]);

    console.log("🔍 Admin API Results:", results);

    const totalRes = results[0].status === "fulfilled" ? results[0].value : null;
    const presentRes = results[1].status === "fulfilled" ? results[1].value : null;
    const leaveRes = results[2].status === "fulfilled" ? results[2].value : null;

    console.log("🔍 Total Employees Response:", totalRes);
    console.log("🔍 Present Today Response:", presentRes);
    console.log("🔍 On Leave Response:", leaveRes);

    return {
      totalEmployees: totalRes?.total_employees || 0,
      presentToday: presentRes?.present_today || 0,
      onLeave: leaveRes?.on_leave || 0
    };
  } catch (error) {
    console.error("Admin Dashboard Service Error:", error);
    return {
      totalEmployees: 0,
      presentToday: 0,
      onLeave: 0
    };
  }
};

/**
 * Fetches dashboard statistics.
 * ⚠️ IMPORTANT:
 * - DO NOT call attendance-summary here (it requires month + year)
 * - Only safe endpoints are used to avoid 422 errors
 */
export const getDashboardStats = async () => {
  try {
    console.log("🔍 Fetching dashboard stats...");
    console.log("Token:", localStorage.getItem("access_token"));
    console.log("Role:", localStorage.getItem("role"));
    
    const results = await Promise.allSettled([
      api.get("/dashboard/total-employees"),
      api.get("/dashboard/weekly-summary", {
        params: { emp_code: getStoredEmpCode() }
      })
    ]);

    console.log("🔍 API Results:", results);

    const totalRes =
      results[0].status === "fulfilled" ? results[0].value : null;

    const weeklyRes =
      results[1].status === "fulfilled" ? results[1].value : null;

    console.log("🔍 Total Employees Response:", totalRes);
    console.log("🔍 Weekly Summary Response:", weeklyRes);

    return {
      totalEmployees: totalRes?.total_employees || 0,
      weeklySummary: weeklyRes || {}
    };
  } catch (error) {
    console.error("Critical Service Layer Error:", error);
    return {
      totalEmployees: 0,
      weeklySummary: {}
    };
  }
};

/* ===================================================================
   2. HEADER DETAILS
   =================================================================== */

/**
 * ❌ REMOVED:
 * getEmployeeHeader
 *
 * Reason:
 * - It was calling the wrong endpoint
 * - It caused /employee/header and /employee-sra/header leaks
 *
 * ✅ Header MUST be fetched ONLY via:
 * getEmployeeHeaderSafe() (defined in employee.service.js)
 */

/* ===================================================================
   3. HOLIDAYS & EVENTS
   =================================================================== */

/**
 * Upcoming Holidays (Dashboard card + scrolling list)
 */
export const getUpcomingHolidays = () =>
  api.get("/holidays/holidays/upcoming");

/**
 * All Holidays
 */
export const getAllHolidays = () =>
  api.get("/holidays/holidays");

/* ===================================================================
   4. ATTENDANCE & CALENDAR
   =================================================================== */

/**
 * Attendance Calendar Data
 */
export const getAttendanceCalendar = () => {
  const emp_code = getStoredEmpCode();
  return api.get("/attendance/calendar", { params: { emp_code } });
};

/**
 * Attendance Trends (Charts)
 */
export const getCheckinTrends = () => {
  const emp_code = getStoredEmpCode();
  return api.get("/dashboard/checkin-trends", { params: { emp_code } });
};

/* ===================================================================
   5. ROLE-SPECIFIC DASHBOARDS
   =================================================================== */

/**
 * Employee Dashboard (exceptions, late entries)
 */
export const getEmployeeDashboard = () => {
  const emp_code = getStoredEmpCode();
  return api.get("/dashboard/my-exceptions", { params: { emp_code } });
};

/**
 * Manager Dashboard
 */
export const getManagerDashboard = () => {
  const emp_code = getStoredEmpCode();
  return api.get("/unified_approval/manager/requests", {
    headers: { 'x-emp-code': emp_code }
  });
};

/**
 * Admin / HR Dashboard
 */
export const getAdminDashboard = () =>
  api.get("/admin/requests");

/* ===================================================================
   6. LEAVE MANAGEMENT
   =================================================================== */

/**
 * Apply Leave (Correct Swagger Endpoint)
 */
export const applyLeaveRequest = (leaveData) => {
  const emp_code = getStoredEmpCode();
  return api.post("/leave/request", leaveData, {
    params: { emp_code }
  });
};

/**
 * ❌ OLD endpoint removed to prevent silent bugs
 * export const applyLeave = ...
 */

/**
 * My Leave / Corrections List
 */
export const getMyLeaveCorrections = () =>
  api.get("/attendance/corrections/my");

/**
 * Submit Attendance / Leave Correction
 */
export const submitAttendanceCorrection = (data) =>
  api.post("/attendance/corrections", data);

/* ===================================================================
   7. SMALL UTILITY EXPORTS
   =================================================================== */

/**
 * Generic GET (debug / experiments)
 */
export const getGeneralApi = (url, params = {}) =>
  api.get(url, { params });
